import { ListProductsDto } from "types/dto/ListProductsDto";
import { CreateProductDto, UpdateProductAttributesDto, UpdateProductWarningsDto, UpdateProductRankedIngredientsDto, UpdateProductAudienceDto, UpdateProductBrandDto, SetProductAvatarMediaDto, SetProductDisplayImagesDto, UpdateProductLeafCategoryDto, UpdateProductAvatarDto, UpdateProductCategoryDto, UpdateProductContentsDto, UpdateProductDto, UpdateProductIngredientsDto, UpdateProductUsagesDto, UploadProductImagesDto } from "../types/dto/CreateProductDto";
import { ApiService } from "./apiService"
import { ProductImageDto } from "types/dto/ProductImageDto";
import { UpdateProductInfoFormValues } from "pages/product/CreateProductPage/updateProductInfoValidationSchema";

export const getProductByBarcode = async (barcode: string) => {
  return await ApiService.get(`products/barcode/${barcode}`);
}

export const getProductStatus = async (barcode: string) => {
  return await ApiService.get(`products/barcodes/${barcode}/status`);
}

export const createProduct = async (data: CreateProductDto) => {
  return await ApiService.post(`products`, data);
};

export const getProducts = async (dto: ListProductsDto) => {
  const barcodeParam = dto.barcode ? `&barcode=${dto.barcode}` : '';
  const nameParam = dto.name ? `&name=${dto.name}` : '';
  const tagParam = dto.tagId ? `&tagId=${dto.tagId}` : '';
  return await ApiService.get(`products?page=${dto.page}&size=${dto.size}${barcodeParam}${nameParam}${tagParam}`);
}

export const getProductById = async (id: string) => {
  return await ApiService.get(`products/${id}`);
}

export const deleteProductImage = async (dto: ProductImageDto) => {
  return await ApiService.delete(`products/${dto.productId}/images/${dto.imageId}`);
}

export const updateProductInfo = async (dto: UpdateProductDto) => {
  return await ApiService.patch(`products/${dto.id}/info`, { ...dto });
};

export const updateProductCategory = async (dto: UpdateProductCategoryDto) => {
  return await ApiService.patch(`products/${dto.id}/category`, { ...dto.data });
};

export const updateProductContents = async (dto: UpdateProductContentsDto) => {
  return await ApiService.patch(`products/${dto.id}/contents`, { ...dto });
};

export const updateProductUsages = async (dto: UpdateProductUsagesDto) => {
  return await ApiService.patch(`products/${dto.id}/usages`, { ...dto });
};

export const updateProductIngredients = async (dto: UpdateProductIngredientsDto) => {
  return await ApiService.patch(`products/${dto.id}/ingredients`, { ...dto });
};

export const updateProductAttributes = async (dto: UpdateProductAttributesDto) => {
  return await ApiService.patch(`products/${dto.id}/attributes`, { ...dto });
};

// Runs the attributes pass again on the raw product this one was promoted from,
// and carries its answer over in place of what the product had.
// The description written again off the pack, on the raw product this product
// came from, and carried onto its two translations. The same pass the review
// card runs, so a product and the row behind it are described by one prompt
// rather than by two that drifted apart.
export const redescribeProduct = async (productId: string) => {
  return await ApiService.patch(`analyzer/products/${productId}/redescribe`, {});
};

// A reviewer can send a run to the description instead of the label. The pass
// does that by itself for a pack that claims nothing at all; this is for the
// pack that claims plenty and claims nothing worth keeping, which only somebody
// looking at the names can tell.
export const reextractProductAttributes = async (
  productId: string,
  withDescription = false,
) => {
  return await ApiService.patch(
    `analyzer/products/${productId}/reextract-attributes${withDescription ? '?withDescription=true' : ''}`,
    {},
  );
};

// The six passes the attributes are read by. A pass answers everything it
// answers at once, so one reload can redraw several lists: the four type lists
// are one question, and so are the colour, the scent and the flavour.
export type AttributePart =
  | 'types'
  | 'concerns'
  | 'goals'
  | 'timings'
  | 'appearance'
  | 'formula-claims';

// One of those passes run again on the raw product this one was promoted from,
// with its answer carried over in place of what the product had for that pass
// alone.
export const reextractProductAttributePart = async ({
  productId,
  part,
  withDescription = false,
}: {
  productId: string;
  part: AttributePart;
  withDescription?: boolean;
}) => {
  return await ApiService.patch(
    `analyzer/products/${productId}/reextract-attributes/${part}${withDescription ? '?withDescription=true' : ''}`,
    {},
  );
};

export const updateProductWarnings = async (dto: UpdateProductWarningsDto) => {
  return await ApiService.patch(`products/${dto.id}/warnings`, { ...dto });
};

export const updateProductRankedIngredients = async (dto: UpdateProductRankedIngredientsDto) => {
  return await ApiService.patch(`products/${dto.id}/ranked-ingredients`, { ...dto });
};

export const updateProductAudience = async (dto: UpdateProductAudienceDto) => {
  return await ApiService.patch(`products/${dto.id}/audience`, { ...dto });
};

export const updateProductBrand = async (dto: UpdateProductBrandDto) => {
  return await ApiService.patch(`products/${dto.id}/brand`, { brandId: dto.brandId });
};

// The avatar picked from a photo the product already has, so nothing is uploaded
// - the server copies the one that was chosen.
export const setProductAvatarMedia = async (dto: SetProductAvatarMediaDto) => {
  return await ApiService.patch(`products/${dto.productId}/avatar-media`, {
    mediaId: dto.mediaId,
  });
};

// The whole selection every time - what is sent is the strip's answer, not one
// photo's.
export const setProductDisplayImages = async (dto: SetProductDisplayImagesDto) => {
  return await ApiService.patch(`products/${dto.productId}/display-images`, {
    mediaIds: dto.mediaIds,
  });
};

// One leaf, picked out of the settled taxonomy - the chain above it is the
// tree's own and needs no restating.
export const updateProductLeafCategory = async (dto: UpdateProductLeafCategoryDto) => {
  return await ApiService.patch(`products/${dto.id}/leaf-category`, {
    leafCategoryId: dto.leafCategoryId,
    ...(dto.tag ? { tag: dto.tag } : {}),
    ...(dto.tagScope ? { tagScope: dto.tagScope } : {}),
  });
};

export const uploadProductImages = async (dto: UploadProductImagesDto) => {
  const { productId, files } = dto;
  const formData = new FormData();

  for (let i = 0; i < files.length; i++) {
    const image = files[i];

    const imageUrl = image.large || image.medium || image.small;
    if (!imageUrl) return;
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    const file = new File([blob], `image-${i + 1}.jpg`, {
      type: blob.type || 'image/jpeg',
    });

    formData.append('files', file, file.name);
  }

  return ApiService.post(`products/${productId}/images`, formData);
};

export const updateProductAvatar = async (dto: UpdateProductAvatarDto) => {
  const { productId, file: avatarFile } = dto;
  const formData = new FormData();

    const image = avatarFile;

    const imageUrl = image.large || image.medium || image.small;
    if (!imageUrl) return;
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    const file = new File([blob], `image.jpg`, {
      type: blob.type || 'image/jpeg',
    });

    formData.append('file', file);
  

  return ApiService.post(`products/${productId}/avatar`, formData);
};

// Pushes the product into the knowledge graph the smart search runs on. Its own
// act rather than something publishing does: a published product is one shoppers
// can see, and being findable by search is a separate question that gets asked
// again every time the product's attributes change.
export const syncProductToGraph = async (productId: string) => {
  return await ApiService.post(`products/${productId}/sync-to-graph`, {});
};

export const publishProduct = async (productId: string) => {
  return await ApiService.patch(`products/${productId}/publish`, {});
};

export const unpublishProduct = async (productId: string) => {
  return await ApiService.patch(`products/${productId}/unpublish`, {});
};
